/* eslint-disable react/no-unused-class-component-methods, react/no-unused-state --
  Page uses this base class state and methods. */
import { Component } from 'react';
import { isEqual } from 'lodash';
import {
  DL_PAGE_NAME_MODAL_ONBOARDING,
  DL_VIRTUAL_PAGE,
} from '@lib/constants';
import { isUserLoggedIn, getFullUserData } from '@lib/user';
import {
  addEventToDataLayer,
} from '@lib/dataLayerUtils';
import { getSignInUrl, getSigninUrlWithOrigin } from '@lib/utils';
import { getFromLocalStorage, setToLocalStorage } from '@lib/localStorage';
import { addAdobeDataEventListeners } from '@lib/atm';
import type { ReactNode } from 'react';
import type { RequestData, UserData, UserSessionData } from '@lib/data-fetching';
import OnboardingModal from '../OnboardingModal/OnboardingModal';

export type PageContainerProps = {
  requestData: RequestData;
  userSessionData: UserSessionData;
};

type PageContainerState = {
  userData: UserData;
  userShopsData: UserSessionData['userShopsData'];
  selectedDropDown?: string;
};

type ModalStyles = { desktop: { content: Record<string, string>; overlay: Record<string, string> };
  mobile: { content: Record<string, string>; overlay: Record<string, string> } };
type ModalHandle = { updateModalBody: (body: ReactNode, isMobile: boolean,
  styles?: ModalStyles, showClose?: boolean) => void };

export type PageStateModifiers = {
  syncUserDataOnPage: () => Promise<void>;
  setModalBody: (body: ReactNode, isMobile?: boolean, styles?: ModalStyles, showClose?: boolean) => void;
  makeAndOpenSignInModal: () => Promise<void>;
  isUserLoggedIn: () => boolean;
  makeAndOpenOnboardingModal: (isMobile?: boolean) => void;
  setActiveFocus: (pageContext: string, id: string) => void;
  setElementFocus: (context: string, itemId: string, element: HTMLElement | null) => void;
};

class PageContainer<P extends PageContainerProps = PageContainerProps> extends Component<P, PageContainerState> {
  protected activeFocus: { pageContext: string; id: string } | null = null;

  protected modal: ModalHandle | null = null;

  protected onboardingModal: object | null = null;

  protected globalPageConfig: object | null = null;

  protected pageStateModifiers: PageStateModifiers;

  constructor(props: P) {
    super(props);
    const {
      userSessionData: {
        userData,
        userShopsData,
      },
    } = this.props;

    this.state = {
      userData: userData || {},
      userShopsData: userShopsData || [],
    };

    this.syncUserDataOnPage = this.syncUserDataOnPage.bind(this);
    this.setModalBody = this.setModalBody.bind(this);
    this.makeAndOpenSignInModal = this.makeAndOpenSignInModal.bind(this);
    this.makeAndOpenOnboardingModal = this.makeAndOpenOnboardingModal.bind(this);
    this.isUserLoggedInFn = this.isUserLoggedInFn.bind(this);
    this.setActiveFocus = this.setActiveFocus.bind(this);
    this.setElementFocus = this.setElementFocus.bind(this);

    this.pageStateModifiers = {
      syncUserDataOnPage: () => this.syncUserDataOnPage(),
      setModalBody: (...args) => this.setModalBody(args[0], args[1] ?? false, args[2], args[3]),
      makeAndOpenSignInModal: () => this.makeAndOpenSignInModal(),
      isUserLoggedIn: () => this.isUserLoggedInFn(),
      makeAndOpenOnboardingModal: (isMobile) => this.makeAndOpenOnboardingModal(isMobile),
      setActiveFocus: this.setActiveFocus,
      setElementFocus: this.setElementFocus,
    };
  }

  componentDidMount() {
    Object.assign(window, { signIn: this.makeAndOpenSignInModal });

    addAdobeDataEventListeners();
  }

  // TODO: investigate why this is an &&
  shouldComponentUpdate(_nextProps: P, nextState: PageContainerState) {
    // Only re-render pages on new userData (favouriting shops/offers)
    const {
      userData: prevUserData,
    } = this.state;
    const shouldUpdate = !isEqual(prevUserData && nextState.userData, undefined);
    return shouldUpdate;
  }

  setActiveFocus = (pageContext: string, id: string) => {
    this.activeFocus = { pageContext, id };
  };

  setElementFocus = (context: string, itemId: string, element: HTMLElement | null) => {
    const { pageContext, id } = (this.activeFocus || {});

    if (context && itemId && context === pageContext && itemId === id) {
      if (element?.focus) element.focus();
      if (window) {
        window.addEventListener('click', this.clearActiveFocus);
        window.addEventListener('keydown', this.clearActiveFocus);
      }
    }
  };

  // TODO: move all modal related methods to Page.js (KD - Jul 24,2017)
  setModalBody(body: ReactNode, isMobile: boolean, styles?: ModalStyles, showClose = true) {
    if (this.modal) {
      this.modal.updateModalBody(body, isMobile, styles, showClose);
    }
  }

  closeOnboardingModal = () => {
    if (!getFromLocalStorage('onboarding_seen')) {
      setToLocalStorage('onboarding_seen', true);
    }
    this.setModalBody(null, false);
  };

  clearActiveFocus = () => {
    this.activeFocus = null;
    if (window) {
      window.removeEventListener('click', this.clearActiveFocus);
      window.removeEventListener('keydown', this.clearActiveFocus);
    }
  };

  // If user choses to browse and not log in, url is created through slugs and urls params from affiliateUrl
  // If user logs in, user is redirected to redirectUrl
  // The promise return is part of the existing page-state modifier contract.
  // eslint-disable-next-line @typescript-eslint/require-await
  makeAndOpenSignInModal = async () => {
    const {
      requestData: {
        urlLocale,
      },
    } = this.props;

    try {
      const signinUrl = getSigninUrlWithOrigin({
        currentPath: window.location.href,
        signInUrl: getSignInUrl(urlLocale),
      });

      window.location.href = signinUrl;
    } catch (error) {
      console.error('[Redirect to sign-in]:', error);
    }
  };

  makeAndOpenOnboardingModal(isMobile = false) {
    const {
      requestData: {
        urlLocale,
      },
    } = this.props;

    const modalBody = this.onboardingModal ? (
      <OnboardingModal
        onboardingModalData={this.onboardingModal}
        isMobile={isMobile}
        locale={urlLocale}
        closeModal={this.closeOnboardingModal}
      />
    ) : null;

    // Add event to dataLayer
    const eventProps = {
      pageName: DL_PAGE_NAME_MODAL_ONBOARDING,
    };
    addEventToDataLayer(DL_VIRTUAL_PAGE, eventProps);

    this.setModalBody(modalBody, isMobile, {
      desktop: {
        content: {
          borderRadius: '16px',
        },
        overlay: {},
      },
      mobile: { content: {}, overlay: {} },
    }, false);
  }

  isUserLoggedInFn() {
    const {
      userSessionData,
    } = this.props;

    return isUserLoggedIn(userSessionData.userData);
  }

  /**
   * Function is called whenever an interaction on the page modifies the user state
   * Examples: Favouriting/Unfavouriting a shop, Saving/Unsaving an offer, on page load if logged in
   *
   * Returns a promise
   ** */
  async syncUserDataOnPage() {
    const {
      userSessionData: {
        userData: userDataProps,
      },
      requestData: {
        urlLocale,
      },
    } = this.props;

    if (!isUserLoggedIn(userDataProps)) {
      return Promise.resolve();
    }

    // // update this.state.userData if logged in
    return getFullUserData(urlLocale).then(({
      userData,
      userShopsData,
    }) => {
      const { userData: currentUserData } = this.state;
      if (!isEqual(userData, currentUserData)) {
        this.setState({
          userData: { ...userData },
          userShopsData,
        });
      }
    });
  }
}

export default PageContainer;
